import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";

export const WIDGET_SETTING_FONT_WEIGHT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: "normal", label: "Normal" },
    { value: "bold", label: "Bold" },
    { value: "lighter", label: "Lighter" },
    { value: "100", label: "100" },
    { value: "200", label: "200" },
    { value: "300", label: "300" },
    { value: "400", label: "400" },
    { value: "500", label: "500" },
    { value: "600", label: "600" },
    { value: "700", label: "700" },
    { value: "800", label: "800" },
    { value: "900", label: "900" },
];

export const WIDGET_SETTING_ALIGNMENT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" },
];

type WidgetSettingSelectProps = {
    value: string;
    onChange: (value: string) => void;
    options?: ReadonlyArray<{ value: string; label: string }>;
    /** Shown when `value` is empty or not found in `options`. */
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    id?: string;
    /** Optional accessible name for the trigger (e.g. when no visible label is associated). */
    "aria-label"?: string;
    /** Optional id of an element that labels the trigger (e.g. visible “Rows per page:” text). */
    "aria-labelledby"?: string;
    /** `top`: list opens above the trigger (e.g. sticky footer with no space below). */
    menuPlacement?: "bottom" | "top";
    /** Render menu in a body portal so scroll/overflow ancestors cannot clip it. */
    menuPortal?: boolean;
    /** Stacking order for the portaled menu (raise above modals; default 1500). */
    menuZIndex?: number;
    /** Render custom trigger label (e.g. provider icon + name). */
    renderValueContent?: (option: { value: string; label: string }) => React.ReactNode;
};

/** Custom list for widget settings — same panel/hover language as font-picker + header search (native select popups are OS-drawn). */
const WidgetSettingSelect: React.FC<WidgetSettingSelectProps> = ({
    value,
    onChange,
    options = WIDGET_SETTING_FONT_WEIGHT_OPTIONS,
    disabled = false,
    className,
    id,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    menuPlacement = "bottom",
    menuPortal = true,
    menuZIndex = 1500,
    placeholder,
    renderValueContent,
}) => {
    const [open, setOpen] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
    const matched = options.find((o) => o.value === value);
    const showPlaceholder = Boolean(placeholder) && (!value || !matched);
    const activeLabel = matched?.label ?? (showPlaceholder ? placeholder! : value);

    const close = useCallback(() => setOpen(false), []);

    const updateMenuPosition = useCallback(() => {
        if (!rootRef.current) {
            return;
        }
        const rect = rootRef.current.getBoundingClientRect();
        const gap = 8;
        // Keep menu at least as wide as longer option labels (narrow triggers used in checklist).
        const menuWidth = Math.max(rect.width, 168);
        if (menuPlacement === "top") {
            setMenuStyle({
                position: "fixed",
                left: rect.left,
                width: menuWidth,
                bottom: window.innerHeight - rect.top + gap,
                zIndex: menuZIndex,
            });
            return;
        }
        setMenuStyle({
            position: "fixed",
            left: rect.left,
            width: menuWidth,
            top: rect.bottom + gap,
            zIndex: menuZIndex,
        });
    }, [menuPlacement, menuZIndex]);

    useLayoutEffect(() => {
        if (!open || !menuPortal) {
            setMenuStyle(null);
            return;
        }
        updateMenuPosition();
        window.addEventListener("scroll", updateMenuPosition, true);
        window.addEventListener("resize", updateMenuPosition);
        return () => {
            window.removeEventListener("scroll", updateMenuPosition, true);
            window.removeEventListener("resize", updateMenuPosition);
        };
    }, [open, menuPortal, updateMenuPosition]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
                return;
            }
            close();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                close();
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("touchstart", onPointerDown, { passive: true });
        window.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("touchstart", onPointerDown);
            window.removeEventListener("keydown", onKey);
        };
    }, [open, close]);

    const renderMenuItems = () =>
        options.map((opt) => (
            <li key={opt.value} role="presentation">
                <button
                    type="button"
                    role="option"
                    aria-selected={opt.value === value}
                    className={classNames(
                        "widget-setting-select__option",
                        "ew-dropdown-option",
                        opt.value === value && "widget-setting-select__option--active",
                        opt.value === value && "ew-dropdown-option--active"
                    )}
                    onClick={() => {
                        onChange(opt.value);
                        close();
                    }}
                >
                    {opt.label}
                </button>
            </li>
        ));

    const menuNode =
        open &&
        (menuPortal ? (
            menuStyle && (
                <ul
                    ref={menuRef}
                    className={classNames(
                        "widget-setting-select__menu",
                        "widget-setting-select__menu--portal",
                        "ew-dropdown-panel",
                        menuPlacement === "top" && "widget-setting-select__menu--portal-top"
                    )}
                    style={menuStyle}
                    role="listbox"
                >
                    {renderMenuItems()}
                </ul>
            )
        ) : (
            <ul className="widget-setting-select__menu" role="listbox">
                {renderMenuItems()}
            </ul>
        ));

    return (
        <>
            <div
                ref={rootRef}
                className={classNames(
                    "widget-setting-select",
                    open && "widget-setting-select--open",
                    menuPlacement === "top" && "widget-setting-select--menu-top",
                    className
                )}
            >
                <button
                    type="button"
                    id={id}
                    className="widget-setting-select__trigger"
                    disabled={disabled}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    onClick={() => !disabled && setOpen((o) => !o)}
                >
                    <span
                        className={classNames(
                            "widget-setting-select__value",
                            showPlaceholder && "widget-setting-select__value--placeholder"
                        )}
                    >
                        {matched && renderValueContent
                            ? renderValueContent(matched)
                            : activeLabel}
                    </span>
                    <span className="widget-setting-select__chevron" aria-hidden />
                </button>
                {!menuPortal && menuNode}
            </div>
            {menuPortal && typeof document !== "undefined" && menuNode
                ? createPortal(menuNode, document.body)
                : null}
        </>
    );
};

export default WidgetSettingSelect;
